// src/components/admin/ModuleCard.jsx - Updated with tab-specific navigation
// #Revised UI_v2 — A6: live metrics on module cards
import React, { useState, useEffect } from 'react';
import { collection, query, where, getCountFromServer, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { ICON_BASE_URL } from '../../utils/moduleIcons';
import { useModules } from '../../contexts/ModuleContext';

function ModuleCard({ module, isActive, onClick, tabCounts = {}, salonId }) {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { isTabAvailable } = useModules();

  // #Revised UI_v2 — A6: start: live metric state + fetch
  const [liveMetric, setLiveMetric] = useState(null);

  useEffect(() => {
    if (!salonId || !module.enabled) return;
    let cancelled = false;

    const fetchMetric = async () => {
      try {
        switch (module.id) {
          case 'bookings': {
            const weekStart = new Date();
            weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Mon
            weekStart.setHours(0, 0, 0, 0);
            const q = query(
              collection(db, 'appointments'),
              where('salonId', '==', salonId),
              where('startDateTime', '>=', Timestamp.fromDate(weekStart))
            );
            const snap = await getCountFromServer(q);
            const n = snap.data().count;
            if (!cancelled) setLiveMetric(`${n} appointment${n !== 1 ? 's' : ''} this week`);
            break;
          }
          case 'home': {
            const q = query(collection(db, 'customers'), where('salonId', '==', salonId));
            const snap = await getCountFromServer(q);
            const n = snap.data().count;
            if (!cancelled) setLiveMetric(`${n} active client${n !== 1 ? 's' : ''}`);
            break;
          }
          case 'promotions': {
            const custQ = query(collection(db, 'customers'), where('salonId', '==', salonId));
            const promoQ = query(collection(db, 'promotions'), where('salonId', '==', salonId));
            const [custSnap, promoSnap] = await Promise.all([
              getCountFromServer(custQ),
              getCountFromServer(promoQ),
            ]);
            const members = custSnap.data().count;
            const promos = promoSnap.data().count;
            if (!cancelled) setLiveMetric(`${members} member${members !== 1 ? 's' : ''} · ${promos} promotion${promos !== 1 ? 's' : ''}`);
            break;
          }
          case 'communications': {
            const q = query(
              collection(db, 'mail'),
              where('salonId', '==', salonId),
              orderBy('createdAt', 'desc'),
              limit(1)
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              const lastCampaign = snap.docs[0].data();
              const sentDate = lastCampaign.createdAt?.toDate
                ? lastCampaign.createdAt.toDate()
                : new Date(lastCampaign.createdAt);
              const daysAgo = Math.floor((Date.now() - sentDate.getTime()) / 86400000);
              const label = daysAgo === 0 ? 'Last sent today'
                : daysAgo === 1 ? 'Last sent yesterday'
                : `Last sent ${daysAgo} days ago`;
              if (!cancelled) setLiveMetric(label);
            } else {
              if (!cancelled) setLiveMetric('No campaigns sent yet');
            }
            break;
          }
          case 'reports': {
            const q = query(
              collection(db, 'appointments'),
              where('salonId', '==', salonId),
              where('status', '==', 'approved')
            );
            const snap = await getDocs(q);
            let revenue = 0;
            snap.forEach(doc => {
              const d = doc.data();
              revenue += Number(d.serviceRequested?.price || d.price || 0);
            });
            const formatted = revenue >= 1000
              ? `$${(revenue / 1000).toFixed(1)}k`
              : `$${revenue.toLocaleString()}`;
            if (!cancelled) setLiveMetric(`${formatted} total booked revenue`);
            break;
          }
          default:
            break;
        }
      } catch {
        // silently ignore — metric stays null
      }
    };

    fetchMetric();
    return () => { cancelled = true; };
  }, [salonId, module.id, module.enabled]);
  // #Revised UI_v2 — A6: end

  const getTotalTabs = () => {
    return module.tabs.length;
  };

  const getModuleStatusBadge = () => {
    if (!module.enabled) {
      return <span className="module-status-badge disabled">Upgrade Required</span>;
    }

    if (isActive) {
      return <span className="module-status-badge active">Active</span>;
    }

    // Don't show any badge for available/enabled modules
    return null;
  };

  const getModuleIcon = () => {
    const iconBaseUrl = ICON_BASE_URL;

    const iconMap = {
      'home': `${iconBaseUrl}core-module.svg?alt=media&token=6a7e6302-93f8-4b42-9495-c9c26f3296da`,
      'promotions': `${iconBaseUrl}loyalty-management.svg?alt=media&token=1f626298-fb6c-4f59-a2b1-5b7c9fa404cd`,
      'communications': `${iconBaseUrl}communication-management.svg?alt=media&token=e8485fc2-cf71-4d76-ab72-6e804212c6b1`,
      'bookings': `${iconBaseUrl}booking-management.svg?alt=media&token=248a2ac6-7a00-4990-82b5-b354ede53ec7`,
      'reports': `${iconBaseUrl}reports-management.svg?alt=media&token=bcfef9fc-6076-42f5-ad60-f633cc51af59`,
      'bespokeai': `${iconBaseUrl}bespoke-ai.svg?alt=media&token=f0b355ff-a133-4ded-a36a-dac8bd0061c5`
    };

    return iconMap[module.id] || null;
  };

  const getModuleDescription = () => {
    switch (module.id) {
      case 'home':
        return 'Manage your business\'s core identity, services, team, and contact information';
      case 'promotions':
        return 'Define your loyalty tiers and earn/ burn rates as well as create targeted promotional campaigns.';
      case 'communications':
        return 'Send email newsletters, push notifications and WhatsApp messages to customers';
      case 'bookings':
        return 'Oversee appointment calendar and booking requests';
      case 'reports':
        return 'View analytics, generate reports, and track performance metrics';
      case 'bespokeai':
        return 'Get AI-powered recommendations and automation for your business operations';
      default:
        return module.description;
    }
  };

  // Get management tools for each module with clickable functionality
  const getManagementTools = () => {
    switch (module.id) {
      case 'home':
        return [
          { name: 'Brand Identity', tabId: 'branding' },
          { name: 'Typography Settings', tabId: 'typography' },
          { name: 'Images', tabId: 'images' },
          { name: 'About Us', tabId: 'aboutUs' },
          { name: 'Contact Information', tabId: 'contact' },
          { name: 'Business Hours', tabId: 'hours' },
          { name: 'Services', tabId: 'services' },
          { name: 'Staff', tabId: 'staff' },
          { name: 'Customers', tabId: 'customers' },
          { name: 'Custom CSS', tabId: 'customCSS' }
        ];
      case 'bookings':
        return [
          { name: 'Calendar Management', tabId: 'booking-calendar' },
          { name: 'Booking Requests', tabId: 'booking-requests' },
          { name: 'Leave Management', tabId: 'leave-management' },
          { name: 'Working Hours', tabId: 'working-hours' },
          // Only a real tab when Bespoke AI (Bookings AI) is enabled; otherwise shown dimmed
          { name: 'Bookings AI', tabId: 'bookings-ai-agent', requiresTab: true }
        ];
      case 'communications':
        return [
          { name: 'Email Campaigns', tabId: 'email-campaigns' },
          { name: 'Push Notification Campaigns', tabId: 'push-campaigns' },
          { name: 'WhatsApp Campaigns', tabId: 'whatsapp-campaigns' },
          { name: 'Portal Personalisation Campaigns', tabId: 'portal-personalisation' },
          { name: 'Campaign History', tabId: 'campaigns-listing' },
          // Only a real tab when Bespoke AI (Communications AI) is enabled; otherwise shown dimmed
          { name: 'Communications AI', tabId: 'communications-ai-agent', requiresTab: true }
        ];
      case 'promotions':
        return [
          { name: 'Loyalty Program', tabId: 'loyalty-program' },
          { name: 'Promotions', tabId: 'promotions' },
          // Only a real tab when Bespoke AI (Loyalty AI) is enabled; otherwise shown dimmed
          { name: 'Loyalty AI', tabId: 'loyalty-ai-agent', requiresTab: true }
        ];
      case 'reports':
        return [
          { name: 'Query Builder', tabId: 'query-builder' },
          { name: 'Analytics Dashboard', tabId: 'analytics-dashboard' },
          { name: 'Revenue Targets', tabId: 'targets' },
          { name: 'Staff Payroll', tabId: 'staff-payroll' },
          // Only a real tab when Bespoke AI (Reports AI) is enabled; otherwise shown dimmed
          { name: 'Reports AI', tabId: 'reports-ai-agent', requiresTab: true }
        ];
      case 'bespokeai':
        return [
          { name: 'Communications AI', tabId: 'communications-ai-agent', targetModule: 'communications', requiresTab: true },
          // The single Loyalty AI lives under Loyalty Management; this opens it
          { name: 'Loyalty AI', tabId: 'loyalty-ai-agent', targetModule: 'promotions', requiresTab: true },
          // The single Bookings AI and Reports AI live under their modules; these open them
          { name: 'Bookings AI', tabId: 'bookings-ai-agent', targetModule: 'bookings', requiresTab: true },
          { name: 'Reports AI', tabId: 'reports-ai-agent', targetModule: 'reports', requiresTab: true }
        ];
      default:
        return [];
    }
  };

  // A tool may open a tab in another module (targetModule), e.g. Bespoke AI > Bookings AI -> Bookings Management
  const isToolLocked = (tool) => tool.requiresTab &&
    (tool.targetModule ? !isTabAvailable(tool.tabId) : !module.tabs.includes(tool.tabId));

  // Handle management tool click - navigate to specific tab
  const handleManagementToolClick = (e, tool) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!module.enabled) {
      setShowUpgradeModal(true);
      return;
    }

    if (isToolLocked(tool)) return;

    // Navigate to the module with a specific tab target
    const moduleToPath = {
      'home': 'core',  // Home card routes to core module page
      'bookings': 'book',
      'communications': 'campaigns',
      'promotions': 'promotion',
      'reports': 'analytics',
      'bespokeai': 'ai'
    };
    
    const path = moduleToPath[tool.targetModule || module.id];
    if (path && tool.tabId) {
      // Store the target tab in sessionStorage so Admin.jsx can pick it up
      sessionStorage.setItem('targetTab', tool.tabId);
      window.location.href = `/BespokeBeauty/${salonId}/admin/${path}`;
    }
  };

  // Handle module click (header/general area) - navigate to module's first tab
  const handleModuleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!module.enabled) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Module is enabled, proceed with normal click to first tab
    if (onClick) {
      console.log('ModuleCard clicking:', module.id, 'enabled:', module.enabled);
      onClick(module.id, module.enabled);
    }
  };

  // #Revised UI_v2 — A10: per-module feature bullets for upgrade modal
  const getUpgradeFeatures = () => {
    switch (module.id) {
      case 'bookings':
        return [
          'Live multi-staff appointment calendar',
          'Automated booking confirmations & reminders',
          'Staff leave & working-hours management',
          'Booking requests approval workflow',
        ];
      case 'promotions':
        return [
          'Configurable loyalty tiers, points & rewards',
          'Targeted promotional campaigns for any segment',
          'Voucher generation & redemption tracking',
          'Loyalty analytics & member insights',
        ];
      case 'communications':
        return [
          'Email, push & WhatsApp campaign builder',
          'Audience segmentation & personalisation',
          'Campaign performance tracking & open rates',
          'Automated transactional notifications',
        ];
      case 'reports':
        return [
          'Real-time revenue & booking dashboards',
          'Client retention & lifetime value reports',
          'Staff performance & payroll summaries',
          'Custom query builder & data exports',
        ];
      case 'bespokeai':
        return [
          'AI-powered booking demand forecasting',
          'Smart campaign audience recommendations',
          'Automated promotion & offer suggestions',
          'Predictive revenue & churn insights',
        ];
      default:
        return [
          'Advanced management tools',
          'Real-time data & analytics',
          'Automated workflows & notifications',
        ];
    }
  };

  // Handle modal close
  const closeModal = (e) => {
    e.stopPropagation();
    setShowUpgradeModal(false);
  };

  // Handle contact sales link
  const handleContactSales = (e) => {
    e.stopPropagation();
    window.open('https://www.bespokecrm.cloud/contact-us', '_blank');
    setShowUpgradeModal(false);
  };

  const managementTools = getManagementTools();

  return (
    <>
      <div 
        className={`module-card ${isActive ? 'active' : ''} ${!module.enabled ? 'disabled' : ''}`}
        onClick={handleModuleClick}
      >
        <div className="module-card-header">
          <div className="module-title-col">
            <h3 className="module-title">
              {module.id === 'promotions' ? 'Loyalty Management' : module.name}
            </h3>
            {/* #Revised UI_v2 — A6: live metric row */}
            {module.enabled && liveMetric && (
              <div className="module-live-metric">
                <span className="live-dot" />
                <span>{liveMetric}</span>
              </div>
            )}
            {!module.enabled && (
              <div className="module-live-metric upgrade-text">
                <span>Upgrade to unlock this module</span>
              </div>
            )}
          </div>
          <div className="module-header-right">
            {getModuleStatusBadge()}
            {getModuleIcon() && (
              <img
                src={getModuleIcon()}
                alt={`${module.name} icon`}
                className="module-icon"
              />
            )}
          </div>
        </div>
        
        <div className="module-card-content">
          <p className="module-description">
            {getModuleDescription()}
          </p>
          
          <div className="module-stats">
            <div className="module-stat">
              <span className="stat-number">{managementTools.length}</span>
              <span className="stat-label">Management Tools</span>
            </div>
          </div>

          {/* Management Tools Grid - 3 columns x 2 rows max */}
          <div className="module-management-tools">
            <h4>Management Tools:</h4>
            <div className="management-tools-grid">
              {/* For Core (home) module, show only 5 tools; for others show 6 */}
              {managementTools.slice(0, module.id === 'home' ? 5 : 6).map((tool, index) => (
                <span
                  key={index}
                  className={`management-tool-item ${isToolLocked(tool) ? 'locked' : 'clickable'}`}
                  onClick={(e) => handleManagementToolClick(e, tool)}
                  title={isToolLocked(tool) ? `${tool.name} requires the Bespoke AI module` : `Navigate to ${tool.name}`}
                  style={isToolLocked(tool) ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                >
                  {tool.name}
                </span>
              ))}
              {/* For Core (home) module, show "+4 more"; for others calculate dynamically */}
              {module.id === 'home' && managementTools.length > 5 && (
                <span className="management-tool-item more-tools">
                  +4 more
                </span>
              )}
              {module.id !== 'home' && managementTools.length > 6 && (
                <span className="management-tool-item more-tools">
                  +{managementTools.length - 6} more
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="module-card-footer">
          <button 
            className={`module-action-button ${module.enabled ? 'primary' : 'disabled'}`}
            onClick={handleModuleClick}
          >
            {!module.enabled ? 'Upgrade Required' : 
             isActive ? 'Currently Active' : 'Open Module'}
          </button>
        </div>
      </div>

      {/* #Revised UI_v2 — A10: start: Enhanced upgrade modal */}
      {showUpgradeModal && (
        <div className="upgrade-modal-overlay" onClick={closeModal}>
          <div className="upgrade-modal-enhanced" onClick={(e) => e.stopPropagation()}>

            {/* Coral gradient header */}
            <div className="upgrade-modal-hero">
              <button className="upgrade-modal-close" onClick={closeModal} aria-label="Close">✕</button>
              <div className="upgrade-modal-lock-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h3 className="upgrade-modal-hero-title">
                {module.id === 'promotions' ? 'Loyalty Management' : module.name}
              </h3>
              <p className="upgrade-modal-hero-sub">
                {getModuleDescription()}
              </p>
            </div>

            {/* Feature checklist */}
            <div className="upgrade-modal-body">
              <p className="upgrade-modal-body-label">What you'll unlock:</p>
              <ul className="upgrade-feature-list">
                {getUpgradeFeatures().map((feature, i) => (
                  <li key={i} className="upgrade-feature-item">
                    <span className="upgrade-feature-check">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <p className="upgrade-trust-line">
                🌟 Trusted by growing salons on the BespokeBeauty platform
              </p>
            </div>

            {/* Actions */}
            <div className="upgrade-modal-footer">
              <button className="upgrade-cta-button" onClick={handleContactSales}>
                Contact Sales — View Pricing →
              </button>
              <button className="upgrade-cancel-link" onClick={closeModal}>
                Maybe later
              </button>
            </div>

          </div>
        </div>
      )}
      {/* #Revised UI_v2 — A10: end */}
    </>
  );
}

export default ModuleCard;